import React, { useCallback, useMemo, useRef } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'

import {
  backgroundEffectKey,
  isBackgroundEffectAvailable,
  preloadBackgroundImage,
  requiresBackgroundImage,
  resolveBackgroundImage,
  type BackgroundEffect,
} from '../services/liveKitVideoBackground'
import { BackgroundPresetGrid, EFFECT_CHOICES } from './BackgroundPresetGrid'
import BackgroundLibraryGrid from './BackgroundLibraryGrid'
import { useBackgroundLibrary } from '../hooks/useBackgroundLibrary'
import {
  ACCEPTED_UPLOAD_TYPES,
  MAX_BACKGROUND_UPLOAD_BYTES,
  MAX_PERSONAL_BACKGROUNDS,
  canHavePersonalBackgrounds,
} from '../services/backgroundLibrary'
import type { BackgroundLibraryKind } from '../services/backgroundLibrary'

/**
 * Load and decode an image effect's picture ahead of the pick.
 *
 * A pick from "No effect" keeps the camera on screen until the image is in,
 * and a pick from another effect keeps that one: either way the wait the user
 * sits through is mostly this load. Started as a tile is pointed at, focused
 * or pressed, it is usually done by the time the click lands.
 *
 * From the cache, without asking the server again: this is not an apply, and
 * the pick itself still checks that the image exists. Best-effort; a failure
 * here is the pick's to report.
 */
function prewarmBackgroundImage(effect: BackgroundEffect): void {
  if (!requiresBackgroundImage(effect)) return
  void resolveBackgroundImage(effect, { revalidate: false })
    .then(({ url }) => (url ? preloadBackgroundImage(url) : undefined))
    .catch(() => undefined)
}

export interface PreJoinBackgroundPickerProps {
  value: BackgroundEffect
  onChange: (effect: BackgroundEffect) => void
  /**
   * What the preview is showing, which differs from `value` while a new pick
   * loads. Still in use until that pick is in: if it fails, this one stays.
   */
  showing?: BackgroundEffect
  /** Whether the camera is on in the preview. */
  cameraEnabled: boolean
  disabled?: boolean
}

/**
 * The choices panel for backgrounds and effects, shown beside the live preview
 * in {@link BackgroundEffectsDialog}.
 *
 * Renders nothing where the feature is switched off for the deployment or the
 * device cannot run segmentation, matching the in-call menu: an inert control
 * raises questions a tooltip cannot answer.
 *
 * Stays usable **with the camera off**, unlike the in-call menu. Choosing up
 * front is legitimate — it just cannot be previewed — so the panel says so
 * rather than blocking it, and the choice still carries into the call.
 */
export const PreJoinBackgroundPicker: React.FC<PreJoinBackgroundPickerProps> = ({
  value,
  onChange,
  showing,
  cameraEnabled,
  disabled = false,
}) => {
  // Evaluated once: neither the build flag nor device capability changes within
  // a session, and the probe touches WebGL.
  const available = useMemo(() => isBackgroundEffectAvailable(), [])
  const library = useBackgroundLibrary(available)
  const personalInputRef = useRef<HTMLInputElement>(null)
  /** Once per image while the picker is open: hovering across tiles is not a reload each time. */
  const prewarmedRef = useRef(new Set<string>())
  const prewarm = useCallback((effect: BackgroundEffect) => {
    const key = backgroundEffectKey(effect)
    if (prewarmedRef.current.has(key)) return
    prewarmedRef.current.add(key)
    prewarmBackgroundImage(effect)
  }, [])

  if (!available) return null

  const activeKey = backgroundEffectKey(value)
  const showingKey = showing && showing.kind !== 'none' ? backgroundEffectKey(showing) : undefined
  const workspaceBackgrounds = library.backgrounds.filter((b) => b.kind === 'workspace')
  const personalBackgrounds = library.backgrounds.filter((b) => b.kind === 'custom')

  const handleFile = async (
    event: React.ChangeEvent<HTMLInputElement>,
    scope: BackgroundLibraryKind
  ) => {
    const file = event.target.files?.[0]
    // Reset first, so picking the same file twice in a row still fires a change.
    event.target.value = ''
    if (!file) return
    const created = await library.upload(file, scope)
    if (created) {
      onChange(
        created.kind === 'workspace'
          ? { kind: 'workspace', backgroundId: created.id }
          : { kind: 'custom', backgroundId: created.id }
      )
    }
  }

  // The image in use cannot be deleted (the grid disables it until another is
  // picked), nor the one still on screen while a pick loads, so a delete never
  // changes the selection.
  const handleDelete = async (id: string) => {
    await library.remove(id)
  }

  const atPersonalCap = personalBackgrounds.length >= MAX_PERSONAL_BACKGROUNDS
  // Personal images belong to whoever holds a credential: a member's session, or a meeting
  // guest's token (SC-439, up to the same cap). With neither, an upload could only be refused,
  // so the section is not offered.
  const canHavePersonal = canHavePersonalBackgrounds()

  const uploadButton = (scope: BackgroundLibraryKind, label: string) => (
    <Button
      size="small"
      variant="outlined"
      startIcon={library.busy ? <CircularProgress size={14} /> : <AddPhotoAlternateIcon />}
      // Stopped here as well as on the server, so the limit is discovered before
      // picking a file rather than after uploading one. Not offered while the
      // count is unknown.
      disabled={disabled || library.busy || atPersonalCap || library.loadFailed}
      onClick={() => personalInputRef.current?.click()}
      data-testid={`background-upload-${scope}`}
      sx={{ textTransform: 'none', mt: 1 }}
    >
      {label}
    </Button>
  )

  return (
    <Box data-testid="prejoin-background-picker">
      {!cameraEnabled && (
        <Typography
          variant="caption"
          sx={{ display: 'block', mb: 1.5, color: 'text.secondary' }}
        >
          Turn on your camera to preview the effect. It will still be applied when you join.
        </Typography>
      )}

      <Typography
        variant="overline"
        sx={{ display: 'block', color: 'text.secondary', letterSpacing: 1 }}
      >
        Blur and effects
      </Typography>

      {/* Same tile shape as the images below, so the panel reads as one grid
          rather than two unrelated controls. */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 0.75,
          mt: 0.75,
          mb: 2,
        }}
      >
        {EFFECT_CHOICES.map((choice) => {
          const key = backgroundEffectKey(choice.effect)
          const selected = key === activeKey
          return (
            <Box
              key={key}
              component="button"
              type="button"
              title={choice.label}
              aria-label={choice.label}
              aria-pressed={selected}
              disabled={disabled}
              data-testid={`prejoin-background-option-${key}`}
              onClick={() => onChange(choice.effect)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                aspectRatio: '4 / 3',
                p: 0.5,
                border: (t) =>
                  `2px solid ${selected ? t.palette.primary.main : t.palette.divider}`,
                borderRadius: 1,
                cursor: disabled ? 'default' : 'pointer',
                background: (t) => t.palette.action.hover,
                color: (t) => (selected ? t.palette.primary.main : t.palette.text.secondary),
                opacity: disabled ? 0.5 : 1,
                '&:hover': { opacity: disabled ? 0.5 : 0.85 },
              }}
            >
              {choice.icon}
              <Typography variant="caption" sx={{ lineHeight: 1.1, textAlign: 'center' }}>
                {choice.label}
              </Typography>
            </Box>
          )
        })}
      </Box>

      <Typography
        variant="overline"
        sx={{ display: 'block', color: 'text.secondary', letterSpacing: 1 }}
      >
        Backgrounds
      </Typography>
      <Box sx={{ mt: 0.75 }}>
        <BackgroundPresetGrid
          activeKey={activeKey}
          onSelect={onChange}
          onPrewarm={prewarm}
          disabled={disabled}
          maxWidth="100%"
        />
      </Box>

      {library.loadFailed && (
        <Alert
          severity="warning"
          sx={{ mt: 2 }}
          data-testid="background-library-load-error"
          action={
            <Button color="inherit" size="small" onClick={library.reload}>
              Retry
            </Button>
          }
        >
          {library.loadError ??
            'Couldn’t load your uploaded backgrounds. Check your connection and retry.'}
        </Alert>
      )}

      {library.error && (
        <Alert severity="warning" onClose={library.clearError} sx={{ mt: 2 }}>
          {library.error}
        </Alert>
      )}

      {workspaceBackgrounds.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="overline"
            sx={{ display: 'block', color: 'text.secondary', letterSpacing: 1 }}
          >
            Company
          </Typography>
          <Box sx={{ mt: 0.75 }}>
            {/* Selectable, not manageable. Company backgrounds are added and
                removed under company settings, so this stays a picker rather
                than becoming a second admin surface that can drift from it. */}
            <BackgroundLibraryGrid
              backgrounds={workspaceBackgrounds}
              activeKey={activeKey}
              onSelect={onChange}
              onPrewarm={prewarm}
              disabled={disabled || library.busy}
            />
          </Box>
        </Box>
      )}

      {canHavePersonal && (
        <Box sx={{ mt: 2 }}>
          <Typography
            variant="overline"
            sx={{ display: 'block', color: 'text.secondary', letterSpacing: 1 }}
          >
            Your images
          </Typography>
          {library.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            personalBackgrounds.length > 0 && (
              <Box sx={{ mt: 0.75 }}>
                <BackgroundLibraryGrid
                  backgrounds={personalBackgrounds}
                  activeKey={activeKey}
                  showingKey={showingKey}
                  onSelect={onChange}
                  onPrewarm={prewarm}
                  disabled={disabled || library.busy}
                  onDelete={(background) => void handleDelete(background.id)}
                />
              </Box>
            )
          )}
          {uploadButton('custom', 'Upload an image')}
          <Typography
            variant="caption"
            sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}
          >
            {library.loadFailed
              ? 'JPEG, PNG or WebP.'
              : atPersonalCap
                ? `You have all ${MAX_PERSONAL_BACKGROUNDS} of your images. Remove one to add another.`
                : `${personalBackgrounds.length} of ${MAX_PERSONAL_BACKGROUNDS} used · JPEG, PNG or WebP, up to ${Math.round(
                    MAX_BACKGROUND_UPLOAD_BYTES / (1024 * 1024)
                  )} MB.`}
          </Typography>
        </Box>
      )}

      {/* Kept out of the layout entirely; the buttons above drive them. */}
      <input
        ref={personalInputRef}
        type="file"
        accept={ACCEPTED_UPLOAD_TYPES.join(',')}
        hidden
        data-testid="background-file-input-custom"
        onChange={(event) => void handleFile(event, 'custom')}
      />
    </Box>
  )
}

export default PreJoinBackgroundPicker
